/**
 * useProjectAggregate — fetch per-project boundary rollup (state | county | cbsa).
 * Mirrors usePortfolioAggregate, scoped to one project.
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../auth/AuthProvider";
import { fetchWithAuth } from "../../../api/http";
import type {
  AggregationLevel,
  AggregateFeatureCollection,
} from "../../portfolio-dashboard/hooks/usePortfolioAggregate";

async function getAggregate(
  token: string,
  projectId: string,
  level: AggregationLevel,
): Promise<AggregateFeatureCollection | null> {
  const params = new URLSearchParams({ level });
  try {
    return await fetchWithAuth<AggregateFeatureCollection>(
      `/projects/${projectId}/airshed-intensity-aggregate?${params.toString()}`,
      token,
    );
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg.includes("404") || msg.toLowerCase().includes("not built")) return null;
    throw err;
  }
}

export function useProjectAggregate(
  projectId: string | undefined,
  level: AggregationLevel | null,
  enabled = true,
) {
  const { token } = useAuth();
  return useQuery<AggregateFeatureCollection | null>({
    queryKey: ["project-aggregate", projectId ?? "none", level ?? "none"],
    queryFn: () => getAggregate(token!, projectId!, level!),
    enabled: Boolean(token) && Boolean(projectId) && Boolean(level) && enabled,
    staleTime: 30 * 60_000,
    gcTime: 2 * 60 * 60_000,
    retry: 0,
  });
}
